import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import type { MediaItem } from "@/components/media/types";

export interface BrandAsset {
  id: string;
  brandId: string;
  type: "IMAGE" | "VIDEO" | "DOCUMENT" | "LOGO" | "FONT";
  url: string;
  thumbnailUrl: string | null;
  filename: string;
  sizeBytes: string; // BigInt serialized
  mimeType: string;
  tags: string[];
  width: number | null;
  height: number | null;
  durationSec: number | null;
  source: string;
  aiGenerated: boolean;
  createdAt: string;
  projectIds: string[];
  /** Set on videos made with Library → Generate video. */
  generation?: AssetGeneration | null;
}

export interface AssetGeneration {
  /** DRAFT = the cheap render; FINAL = its 1080p upscale, ready to post. */
  stage: "DRAFT" | "FINAL";
  model: string | null;
  prompt: string | null;
  aspectRatio: "9:16" | "16:9" | "1:1" | null;
  durationSeconds: number | null;
  resolution: string;
  referenceAssetIds: string[];
  parentAssetId: string | null;
  costUsd: number | null;
  /** For a DRAFT: its 1080p version, when one exists. */
  finalAssetId?: string | null;
}

/** The composer media-kind for an asset, or null for non-postable assets
 *  (DOCUMENT / FONT / LOGO) that must not be attached to a post. */
export function assetToMediaKind(type: BrandAsset["type"]): "image" | "video" | null {
  if (type === "IMAGE") return "image";
  if (type === "VIDEO") return "video";
  return null;
}

/** Adapt a BrandAsset to the source-agnostic MediaItem the media components use. */
export function assetToMediaItem(a: BrandAsset): MediaItem {
  return {
    id: a.id,
    type: a.type,
    url: a.url,
    thumbnailUrl: a.thumbnailUrl,
    durationSec: a.durationSec,
    filename: a.filename,
  };
}

export interface CreateAssetInput {
  url: string;
  type: "IMAGE" | "VIDEO" | "DOCUMENT";
  filename: string;
  sizeBytes: number;
  mimeType: string;
  width?: number;
  height?: number;
  durationSec?: number;
  thumbnailUrl?: string;
  tags?: string[];
  projectIds?: string[];
}

export function useBrandAssets(
  brandId: string,
  filters: { projectId?: string | null; type?: string | null } = {},
) {
  const params = new URLSearchParams();
  if (filters.projectId) params.set("projectId", filters.projectId);
  if (filters.type) params.set("type", filters.type);
  const qs = params.toString();
  return useQuery({
    queryKey: ["brand-assets", brandId, filters.projectId ?? "", filters.type ?? ""],
    queryFn: () =>
      apiFetch<BrandAsset[]>(
        `/brands/${brandId}/assets${qs ? "?" + qs : ""}`,
      ),
    enabled: !!brandId,
    staleTime: 30_000,
  });
}

export function useCreateBrandAsset(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssetInput) =>
      apiFetch<BrandAsset>(`/brands/${brandId}/assets`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brand-assets", brandId] });
    },
  });
}

export function useDeleteBrandAsset(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) =>
      apiFetch<void>(`/brands/${brandId}/assets/${assetId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brand-assets", brandId] });
    },
  });
}
